import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PublicLayout from "@/components/PublicLayout";
import PriceCalculator from "@/components/PriceCalculator";
import TrustBadges from "@/components/TrustBadges";
import ReviewsSection from "@/components/ReviewsSection";
import { CheckCircle2, MapPin, Home as HomeIcon, Building2, Truck, Package, Warehouse } from "lucide-react";

const CITIES = [
  { label: "Noida", to: "/packers-movers-noida", sub: "Sec 62, 137, Expressway" },
  { label: "Greater Noida", to: "/packers-movers-greater-noida", sub: "Pari Chowk, Alpha, Beta" },
  { label: "Ghaziabad", to: "/packers-movers-ghaziabad", sub: "Indirapuram, Vaishali, Vasundhara" },
  { label: "Gurugram", to: "/packers-movers-gurugram", sub: "DLF, Cyber City, Sohna Rd" },
  { label: "Faridabad", to: "/packers-movers-faridabad", sub: "Sec 21, NIT, Neharpar" },
  { label: "Delhi", to: "/packers-movers-delhi", sub: "Dwarka, Rohini, South Ex" },
];

const SERVICES = [
  { icon: HomeIcon, title: "Household Shifting", desc: "1 RK se Villa tak — safe, insured, on-time relocation across NCR." },
  { icon: Building2, title: "Office Relocation", desc: "Zero downtime corporate moves. Weekend & night shifts available." },
  { icon: Truck, title: "Intercity Movers", desc: "NCR to Lucknow, Jaipur, Mumbai, Bangalore — GPS tracked trucks." },
  { icon: Package, title: "Packing Only", desc: "Professional 5-layer packing with bubble wrap, carton, stretch film." },
  { icon: Warehouse, title: "Storage & Warehousing", desc: "Short & long-term dust-free, CCTV-monitored storage in Noida." },
  { icon: Truck, title: "Car / Bike Transport", desc: "Enclosed carrier for two-wheelers & sedans. Full insurance cover." },
];

export default function HomePage() {
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E3A8A]">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1681004478577-cb7f8421f78c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtb3ZpbmclMjB0cnVjayUyMHBhY2tpbmclMjBtb3ZlcnMlMjBsb2dpc3RpY3MlMjBpbmRpYXxlbnwwfHx8fDE3ODg0ODE3MzR8MA&ixlib=rb-4.1.0&q=85)", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="text-white fade-in-up">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/40 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-200 mb-4">
              🪔 Diwali Offer Live — Flat 10% Off + Free Bubble Wrap
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Delhi NCR's <span className="text-[#F97316]">Most Trusted</span> Packers & Movers
            </h1>
            <p className="mt-4 text-lg text-slate-200 max-w-2xl">
              15+ saal ka bharosa. 50,000+ khush parivar. Insured, GST-registered, on-time — <span className="font-semibold text-white">Noida se Gurugram, Ghaziabad se Delhi</span> — hum har jagah pahunchte hain.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full shadow-xl" data-testid="hero-quick-quote-button">
                <Link to="/contact">Get Free Quote →</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full bg-white/10 text-white border-white/40 hover:bg-white/20" data-testid="hero-services-button">
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[["50,000+", "Happy Moves"], ["4.9★", "2,480 Reviews"], ["25+", "Own Trucks"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#F97316]">{n}</div>
                  <div className="text-xs text-slate-300">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-in-up">
            <PriceCalculator />
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* City grid */}
      <section className="py-16 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F172A]">Serving Every Corner of Delhi NCR</h2>
          <p className="text-slate-600 mt-2">Local team in each city — fast response, area expertise</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-grid">
          {CITIES.map((c) => (
            <Link key={c.to} to={c.to} className="group" data-testid={`home-city-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
              <Card className="p-6 hover:border-[#F97316] hover:shadow-xl transition-all border-2 border-transparent bg-white h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-5 h-5 text-[#F97316]" />
                      <h3 className="font-display font-bold text-xl text-[#0F172A]">Packers Movers {c.label}</h3>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{c.sub}</p>
                  </div>
                  <span className="text-[#F97316] font-bold text-2xl group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F172A]">Every Shifting Need, Solved</h2>
            <p className="text-slate-600 mt-2">Ghar ho ya office, ek shehar ho ya doosra — sab hum sambhal lenge.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Card key={i} className="p-6 hover:shadow-lg transition-shadow bg-white border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EAB308] grid place-items-center text-white mb-3">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-[#0F172A]">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* Why us */}
      <section className="py-16 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F172A]">Why Maa Vindhyavasini is Different</h2>
          <p className="text-slate-600 mt-3">Delhi NCR mein bahut packers hain — hum ye 5 cheezein promise karte hain:</p>
          <ul className="mt-6 space-y-3">
            {[
              "No hidden charges — final price = quoted price",
              "5-layer packing (bubble wrap + carton + stretch film) included",
              "Transit insurance up to ₹5 lakh included in Premium plan",
              "GPS-tracked own fleet — no third-party truck surprise",
              "24×7 owner-handled WhatsApp — direct line to accountability",
            ].map((p) => (
              <li key={p} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-none mt-0.5" />
                <span className="text-slate-700">{p}</span>
              </li>
            ))}
          </ul>
          <Button asChild className="mt-6 bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="why-us-quote-btn">
            <Link to="/contact">Book Free Home Survey</Link>
          </Button>
        </div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
          <img src="https://images.unsplash.com/photo-1714647211902-bb711d643a17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxob3VzZSUyMHBhY2tpbmclMjBjYXJkYm9hcmQlMjBib3hlcyUyMHJlbG9jYXRpb258ZW58MHx8fHwxNzg4NDgxNzM0fDA&ixlib=rb-4.1.0&q=85" alt="Professional packing" className="w-full h-full object-cover" />
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-xl px-4 py-2 shadow-lg">
            <div className="text-xs text-slate-500">Our Team</div>
            <div className="font-display font-bold text-[#0F172A]">Trained. Trusted. On Time.</div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
